import { useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "@astryxdesign/core/AppShell";
import { TopNav, TopNavHeading } from "@astryxdesign/core/TopNav";
import { SideNav, SideNavItem, SideNavSection } from "@astryxdesign/core/SideNav";
import { HStack } from "@astryxdesign/core/HStack";
import { Badge } from "@astryxdesign/core/Badge";
import { Divider } from "@astryxdesign/core/Divider";
import { DropdownMenu } from "@astryxdesign/core/DropdownMenu";
import { MoreMenu } from "@astryxdesign/core/MoreMenu";
import { Center } from "@astryxdesign/core/Center";
import { Spinner } from "@astryxdesign/core/Spinner";
import { Section } from "@astryxdesign/core/Section";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Button } from "@astryxdesign/core/Button";
import { Theme } from "@astryxdesign/core/theme";
import { madhaniTheme } from "./theme/madhani";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Static asset served from public/, not imported — same reasoning as any
// public/ file in Vite: it needs no bundling or hashing, just a stable URL.
// The app's own mark (also the favicon/apple-touch-icon source — see
// index.html) rather than the Madhani company logo, which is reserved for
// the login page's brand moment instead — see LoginPage.tsx.
const APP_ICON_URL = "/images/app-icon.svg";
import {
  Video,
  MonitorPlay,
  MapPin,
  Users as UsersIcon,
  ShieldCheck,
  ShieldAlert,
  UserRound,
  Settings,
  LogOut,
  Monitor,
  Sun,
  Moon,
} from "lucide-react";
import { LiveGridPage } from "./features/cameras/LiveGridPage";
import { CamerasPage } from "./features/cameras/CamerasPage";
import { UsersPage } from "./features/users/UsersPage";
import { RolesPage } from "./features/roles/RolesPage";
import { ProjectsPage } from "./features/projects/ProjectsPage";
import { OidcSettingsPage } from "./features/settings/OidcSettingsPage";
import { AccountDialog } from "./features/auth/AccountDialog";
import { LoginPage } from "./features/auth/LoginPage";
import { useAuth } from "./features/auth/AuthProvider";
import { useHealthEvents } from "./features/health/useHealthEvents";
import { isServerLive } from "./features/stream/serverHealth";
import { useToast } from "@astryxdesign/core/Toast";
import type { StreamNotice } from "./lib/api";
import { ColorModeToggle } from "./features/theme/ColorModeToggle";
import { useColorMode } from "./lib/useColorMode";
import { useIsNarrowViewport } from "./lib/responsive";
import { RouterLink } from "./lib/RouterLink";
import { type Permission, hasPermission } from "./lib/api";

export default function App() {
  const [colorMode, setColorMode] = useColorMode();
  const { isLoading } = useAuth();

  // The root Theme (no parent Theme above it) syncs data-theme to <html>,
  // which is what makes portaled content — Dialog, AlertDialog, the toast
  // viewport — pick up the right mode too, even though they render outside
  // this component tree. Wrapping every branch below (loading, login,
  // signed-in) means the color mode choice is never lost across a sign-in.
  //
  // BrowserRouter wraps Theme (not the other way around) so a signed-out
  // LoginPage — which has no reason to use routing today, but shouldn't be
  // structurally prevented from redirecting post-login — sits in the same
  // router context as the authenticated Dashboard below it.
  return (
    <BrowserRouter>
      <Theme theme={madhaniTheme} mode={colorMode}>
        {isLoading ? (
          <Center height="100dvh">
            <Spinner size="lg" label="Loading…" />
          </Center>
        ) : (
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <Dashboard colorMode={colorMode} onColorModeChange={setColorMode} />
                </RequireAuth>
              }
            />
          </Routes>
        )}
      </Theme>
    </BrowserRouter>
  );
}

// A signed-in user who navigates (or is redirected) to /login belongs back
// wherever they were headed, not just at the root — `state.from` is set by
// RequireAuth below when it bounces an unauthenticated visit.
function LoginRoute() {
  const { user } = useAuth();
  const location = useLocation();
  if (user) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/live";
    return <Navigate to={from} replace />;
  }
  return <LoginPage />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

// Closes a gap routing introduces: before real URLs existed, a viewer
// without a given permission just never saw the nav item, so there was no
// way to *reach* a page they couldn't use. A typed or bookmarked URL now
// needs an honest response instead of the blank pane that would render if
// this guard didn't exist (the page component itself assumes its permission
// check already passed).
function RequirePermission({ perm, children }: { perm: Permission | Permission[]; children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const perms = Array.isArray(perm) ? perm : [perm];
  if (!user || perms.some((p) => hasPermission(user, p))) return <>{children}</>;
  return (
    <Section padding={8}>
      <EmptyState
        icon={<ShieldAlert size={32} />}
        title="You don't have access to this page"
        description="Ask an admin if you need this permission, or head back to Live cameras."
        actions={<Button label="Go to Live cameras" variant="primary" onClick={() => navigate("/live")} />}
      />
    </Section>
  );
}

interface DashboardProps {
  colorMode: ReturnType<typeof useColorMode>[0];
  onColorModeChange: ReturnType<typeof useColorMode>[1];
}

// Split out from App so the hooks below (useHealthEvents, the route table)
// only ever run once a user is actually signed in — an SSE connection and
// camera polling have no reason to start against an API that will just 401.
function Dashboard({ colorMode, onColorModeChange }: DashboardProps) {
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Notices are surfaced here rather than on the live page because they are
  // addressed to the PERSON, not to a tile: an operator on the Cameras page
  // whose colleague just stopped a stream still needs to know, and a toast that
  // only fired while the wall happened to be open would tell them nothing.
  const showToast = useToast();
  const health = useHealthEvents();
  useEffect(
    () =>
      health.subscribeNotice((notice: StreamNotice) => {
        if (notice.type !== "stream_evicted") return;
        const who = notice.actor ? `${notice.actor} stopped` : "Someone stopped";
        // Deliberately the default "info" tone rather than "error". Nothing
        // failed — a colleague made a legitimate call — and error toasts here
        // persist until dismissed, so a busy shift would bury the room in red
        // banners nobody reads by the end of it.
        showToast({
          body: `${who} ${notice.camera_name} to make room for ${notice.started_camera_name}.`,
        });
      }),
    [health.subscribeNotice, showToast],
  );

  if (!user) return null; // RequireAuth only renders Dashboard when user is set.

  // Deliberately keeps reporting the last known snapshot while
  // health.phase is "interrupted" rather than collapsing to 0 — byCamera
  // isn't cleared on interruption, only phase changes (see useHealthEvents).
  // A wall that looks like it emptied itself is worse than a stale number
  // with the "Reconnecting…" badge next to it as the qualifier.
  const liveCount = Array.from(health.byCamera.values()).filter((s) => isServerLive(s)).length;
  const canManageCameras = hasPermission(user, "cameras.manage");
  const canManageProjects = hasPermission(user, "projects.manage");
  const canManageUsers = hasPermission(user, "users.manage") || hasPermission(user, "users.view");
  const canManageRoles = hasPermission(user, "roles.manage");
  const canManageSystem = hasPermission(user, "system.manage");

  // startsWith, not exact match, so /live/:projectCode still highlights "Live".
  const isSelected = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Below this width the full endContent (two status badges + a divider + a
  // 3-way segmented control + the account menu) overflows and pushes
  // AppShell's own mobile nav toggle off-screen.
  //
  // Folding the status controls into a MoreMenu was not enough on its own:
  // the account trigger renders the display name, and "Administrator" alone
  // is 163px of a 375px row — measured, the row still ran to x=411 and the
  // nav toggle was clipped past the viewport edge, leaving no way to reach
  // any page but this one on a phone. So on narrow screens the account items
  // fold into the SAME menu rather than sitting beside it: one icon-only
  // trigger, whose width cannot grow with somebody's name.
  const isNarrow = useIsNarrowViewport();

  const accountItems = [
    { label: "My account", icon: <Settings size={14} />, onClick: () => setIsAccountOpen(true) },
    { type: "divider" as const },
    { label: "Sign out", icon: <LogOut size={14} />, onClick: () => void logout(), variant: "destructive" as const },
  ];

  const accountMenu = (
    <DropdownMenu
      button={{ label: user.display_name || user.username, icon: <UserRound size={16} />, variant: "ghost" }}
      items={accountItems}
    />
  );

  return (
    <AppShell
      height="fill"
      contentPadding={0}
      topNav={
        <TopNav
          label="Primary"
          heading={
            <TopNavHeading
              logo={<img src={APP_ICON_URL} alt="" width={24} height={24} />}
              heading="Madhani CCTV"
              subheading="Live site monitoring"
            />
          }
          endContent={
            isNarrow ? (
              <MoreMenu
                label="Account, status and display options"
                items={[
                  ...(liveCount > 0
                    ? [{ label: `${liveCount} camera${liveCount === 1 ? "" : "s"} live`, isDisabled: true }]
                    : []),
                  ...(health.phase === "interrupted" ? [{ label: "Health feed reconnecting…", isDisabled: true }] : []),
                  { type: "divider" as const },
                  { label: "Match system theme", icon: <Monitor size={14} />, onClick: () => onColorModeChange("system") },
                  { label: "Light theme", icon: <Sun size={14} />, onClick: () => onColorModeChange("light") },
                  { label: "Dark theme", icon: <Moon size={14} />, onClick: () => onColorModeChange("dark") },
                  { type: "divider" as const },
                  // The signed-in account is a disabled row rather than a
                  // heading: on a phone the trigger is a bare icon, so
                  // without this there is nothing anywhere that says who you
                  // are signed in as.
                  { label: user.display_name || user.username, icon: <UserRound size={14} />, isDisabled: true },
                  ...accountItems,
                ]}
              />
            ) : (
              <HStack gap={2} vAlign="center">
                {liveCount > 0 && <Badge variant="success" label={`${liveCount} live`} />}
                {health.phase === "interrupted" && <Badge variant="warning" label="Reconnecting…" />}
                <Divider orientation="vertical" />
                <ColorModeToggle value={colorMode} onChange={onColorModeChange} />
                {accountMenu}
              </HStack>
            )
          }
        />
      }
      // Collapsed to its icon rail on first load. This is a wall-watching
      // tool: the live grid is what the screen is for, and five nav items
      // read once a session should not hold a strip of it open by default.
      // The built-in toggle expands it, and the choice is deliberately not
      // persisted — defaultIsCollapsed is a starting state, so every session
      // begins with the video as wide as it can be.
      sideNav={
        <SideNav collapsible={{ defaultIsCollapsed: true }}>
          <SideNavSection title="Menu" isHeaderHidden>
            <SideNavItem as={RouterLink} href="/live" label="Live" icon={MonitorPlay} isSelected={isSelected("/live")} />
            {canManageCameras && (
              <SideNavItem as={RouterLink} href="/cameras" label="Cameras" icon={Video} isSelected={isSelected("/cameras")} />
            )}
            {canManageProjects && (
              <SideNavItem
                as={RouterLink}
                href="/projects"
                label="Projects"
                icon={MapPin}
                isSelected={isSelected("/projects")}
              />
            )}
            {canManageUsers && (
              <SideNavItem as={RouterLink} href="/users" label="Users" icon={UsersIcon} isSelected={isSelected("/users")} />
            )}
            {canManageRoles && (
              <SideNavItem
                as={RouterLink}
                href="/roles"
                label="Roles"
                icon={ShieldCheck}
                isSelected={isSelected("/roles")}
              />
            )}
            {canManageSystem && (
              <SideNavItem
                as={RouterLink}
                href="/settings/sso"
                label="Single sign-on"
                icon={Settings}
                isSelected={isSelected("/settings/sso")}
              />
            )}
          </SideNavSection>
        </SideNav>
      }
    >
      {/* Keyed by pathname so navigating away from a page that crashed
          remounts a fresh boundary instead of the fallback screen sticking
          around forever — a class component's local state doesn't reset on
          its own just because its children swapped underneath it. */}
      <ErrorBoundary key={location.pathname}>
      <Routes>
        <Route path="/" element={<Navigate to="/live" replace />} />
        <Route
          path="/live"
          element={<LiveGridPage health={health} onManageCameras={canManageCameras ? () => navigate("/cameras") : undefined} />}
        />
        <Route
          path="/live/:projectCode"
          element={<LiveGridPage health={health} onManageCameras={canManageCameras ? () => navigate("/cameras") : undefined} />}
        />
        <Route
          path="/cameras"
          element={
            <RequirePermission perm="cameras.manage">
              <CamerasPage health={health} />
            </RequirePermission>
          }
        />
        <Route
          path="/projects"
          element={
            <RequirePermission perm="projects.manage">
              <ProjectsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/users"
          element={
            <RequirePermission perm={["users.view", "users.manage"]}>
              <UsersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/roles"
          element={
            <RequirePermission perm="roles.manage">
              <RolesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/sso"
          element={
            <RequirePermission perm="system.manage">
              <OidcSettingsPage />
            </RequirePermission>
          }
        />
        <Route
          path="*"
          element={
            <Section padding={8}>
              <EmptyState
                icon={<MapPin size={32} />}
                title="Page not found"
                description="That page doesn't exist, or you followed a stale link."
                actions={<Button label="Go to Live cameras" variant="primary" onClick={() => navigate("/live")} />}
              />
            </Section>
          }
        />
      </Routes>
      </ErrorBoundary>

      <AccountDialog isOpen={isAccountOpen} onOpenChange={setIsAccountOpen} />
    </AppShell>
  );
}
